import { isEqual as deepEqual } from "lodash";
import React from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import classNames from "classnames";

import {
  st,
  Link,
  SubBar,
  MainView,
  SidebarView,
  StLogo,
  Button,
  FormControl,
  Label,
  AutoTranslateButton,
  Modal,
  ProductsRepository,
} from "lib/index";
import { Loader } from "lib/components/vendor";
import { createAlert, forceLeftBarToggle } from "modules/app/actions";
import {
  STEPS,
  buildProductUrl,
  initializeView,
  fetchProduct,
  updateProduct,
  requestStep,
  remove,
  validate,
  save,
  setLocales,
  toggleSidebar,
  changeEditedLocale,
  updateTranslatedProduct,
} from "./index";
import { addWindowOnBeforeUnloadCallback } from "modules/utils/index";
import StepAccommodationSettingsForm from "./StepAccommodationSettings/StepAccommodationSettingsForm";
import StepAdditionalForm from "./StepAdditional/StepAdditionalForm";
import StepAvailabilityForm from "./StepAvailability/StepAvailabilityForm";
import StepBookingSettingsForm from "./StepBookingSettings/StepBookingSettingsForm";
import StepCustomerFieldsForm from "./StepCustomerFields/StepCustomerFieldsForm";
import StepDescriptionForm from "./StepDescription/StepDescriptionForm";
import StepLocationForm from "./StepLocation/StepLocationForm";
import StepPaymentsForm from "./StepPayments/StepPaymentsForm";
import StepPhotosForm from "./StepPhotos/StepPhotosForm";
import StepPricedFieldsForm from "./StepPricedFields/StepPricedFieldsForm";
import StepPricelistForm from "./StepPricelist/StepPricelistForm";
import openManageLocalesPrompt from "./ManageLocalesPrompt";
import StepIntegrations from "./StepIntegrations/StepIntegrations";
import handleError from "lib/utils/errors/handleError";
import { store } from "app/index";
import { UPDATE_PRODUCT_FAILURE } from "../utils";

@connect(
  (state) => ({
    currentCompanyId: state.auth.company.model.id,
    sidebarExtended: state.products.EditProductView.sidebarExtended,
    forceSidebarVisibility: state.products.EditProductView.forceSidebarVisibility,
    initialProduct: state.products.EditProductView.initialProduct,
    product: state.products.EditProductView.product,
    currentStepParams: state.products.EditProductView.currentStepParams,
    steps: state.products.EditProductView.steps,
    fetchDone: state.products.EditProductView.fetchDone,
    fetchLoading: state.products.EditProductView.fetchLoading,
    fetchFailed: state.products.EditProductView.fetchFailed,
    dirty: state.products.EditProductView.dirty,
    valid: state.products.EditProductView.valid,
    submitLoading: state.products.EditProductView.submitLoading,
    removeDone: state.products.EditProductView.removeDone,
    editedLocale: state.products.EditProductView.editedLocale,
  }),
  (dispatch) =>
    bindActionCreators(
      {
        forceLeftBarToggle,
        initializeView,
        fetchProduct,
        updateProduct,
        requestStep,
        remove,
        validate,
        save,
        setLocales,
        toggleSidebar,
        changeEditedLocale,
        updateTranslatedProduct,
      },
      dispatch
    )
)
@withRouter
export default class EditProductView extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isModalOpen: false,
      sourceLanguage: null,
      isTranslating: false,
    };
  }
  subscriptions = [];

  componentDidMount() {
    this.props.forceLeftBarToggle(false);
    this._respondToProps();

    addWindowOnBeforeUnloadCallback(this, () => {
      const { product, dirty, removeDone } = this.props;
      if (this.needsExitConfirmation()) {
        return I18n.t("papp.common.discard_changes_confirm_message");
      }
    });
  }

  componentDidUpdate(prevProps) {
    this._respondToProps(prevProps);
  }

  componentWillUnmount() {
    this.props.forceLeftBarToggle(null);
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.subscriptions.length = 0;
  }

  _respondToProps(prevProps) {
    if (!prevProps || prevProps.params.id !== this.props.params.id) {
      this.props.initializeView({ id: this.props.params.id });
    }
    if (!prevProps || prevProps.location.query.step !== this.props.location.query.step) {
      this.props.requestStep({
        id: this.props.location.query.step,
      });
    }
  }

  needsExitConfirmation() {
    return !this.props.removeDone && this.props.dirty;
  }

  confirmViewExit() {
    const { updateProduct, initialProduct } = this.props;
    if (window.confirm(I18n.t("papp.common.discard_changes_confirm_message"))) {
      updateProduct(initialProduct);
    } else {
      return false;
    }
  }

  renderStepContent() {
    const { product, currentStepParams, editedLocale, save } = this.props;
    if (!currentStepParams || !product) {
      return;
    }
    const readOnlyAccess = !st.authorize("update", product);

    switch (currentStepParams.id) {
      case STEPS.STEP_AVAILABILITY:
        return <StepAvailabilityForm product={product} readOnlyAccess={readOnlyAccess} />;
      case STEPS.STEP_DESCRIPTION:
        return product.available_locales.includes(editedLocale) ? (
          <StepDescriptionForm key={editedLocale} locale={editedLocale} readOnlyAccess={readOnlyAccess} isTranslating={this.state.isTranslating} />
        ) : null;
      case STEPS.STEP_CUSTOMER_FIELDS:
        return <StepCustomerFieldsForm editedLocale={editedLocale} readOnlyAccess={readOnlyAccess} />;
      case STEPS.STEP_PRICED_FIELDS:
        return <StepPricedFieldsForm editedLocale={editedLocale} readOnlyAccess={readOnlyAccess} />;
      case STEPS.STEP_PRICELIST:
        return <StepPricelistForm editedLocale={editedLocale} readOnlyAccess={readOnlyAccess} />;
      case STEPS.STEP_PAYMENTS:
        return <StepPaymentsForm editedLocale={editedLocale} readOnlyAccess={readOnlyAccess} />;
      case STEPS.STEP_LOCATION:
        return <StepLocationForm editedLocale={editedLocale} readOnlyAccess={readOnlyAccess} />;
      case STEPS.STEP_PHOTOS:
        return <StepPhotosForm editedLocale={editedLocale} readOnlyAccess={readOnlyAccess} />;
      case STEPS.STEP_ADDITIONAL:
        return <StepAdditionalForm editedLocale={editedLocale} readOnlyAccess={readOnlyAccess} />;
      case STEPS.STEP_BOOKING_SETTINGS:
        return <StepBookingSettingsForm editedLocale={editedLocale} readOnlyAccess={readOnlyAccess} />;
      case STEPS.STEP_ACCOMMODATION_SETTINGS:
        return <StepAccommodationSettingsForm readOnlyAccess={readOnlyAccess} />;
      case STEPS.STEP_INTEGRATIONS:
        return <StepIntegrations readOnlyAccess={readOnlyAccess} />;
      default:
        return null;
    }
  }

  generateSidemenuLinks() {
    const { currentCompanyId, initialProduct, product, fetchDone, steps, setLocales, currentStepParams } = this.props;
    if (!fetchDone || !steps.length || !product) {
      return [];
    }
    const readOnlyAccess = !st.authorize("update", product);

    const createLink = ({ key, label, icon, stepParams, children, onClick, visible = true }) => {
      if (!visible) {
        return null;
      }
      let step, path;
      if (stepParams) {
        step = steps.find((step) => deepEqual(step.params, stepParams));
        path = buildProductUrl(currentCompanyId, product, step && step.params ? { step: step.params.id } : undefined);
        onClick = (event) => {
          const stepId = step && step.params && step.params.id;
          if (
            stepId &&
            stepId !== currentStepParams.id &&
            stepId === STEPS.STEP_AVAILABILITY &&
            this.needsExitConfirmation() &&
            this.confirmViewExit() === false
          ) {
            event.preventDefault();
            event.stopPropagation();
            return false;
          }
        };
      }
      return {
        key: key || JSON.stringify(stepParams),
        label,
        icon,
        error: step ? !step.meta.valid : false,
        active: currentStepParams && step && deepEqual(step.params, currentStepParams),
        path,
        onClick,
        children: children && children.map(createLink).filter((l) => !!l),
      };
    };

    const isStandard = product.product_type === "standard";
    const isAccommodation = initialProduct.product_type === "accommodation";
    const isAdmin = ["global_admin", "main_admin", "editor_admin"].includes(st.authorize.member.role);

    const menuSteps = [
      {
        stepParams: { id: STEPS.STEP_DESCRIPTION },
        label: I18n.t("papp.products.EditProductView.menu.description"),
        icon: <i className="icon material-icons">description</i>,
        children: [
          {
            stepParams: { id: STEPS.STEP_ACCOMMODATION_SETTINGS },
            label: I18n.t("papp.products.EditProductView.menu.accommodation_settings"),
            visible: initialProduct.product_type === "accommodation",
          },
        ],
      },
      {
        stepParams: { id: isAccommodation ? STEPS.STEP_BOOKING_SETTINGS : STEPS.STEP_AVAILABILITY },
        label: I18n.t("papp.products.EditProductView.menu.pricelist"),
        icon: <i className="icon material-icons">date_range</i>,
        children: [
          {
            stepParams: { id: STEPS.STEP_AVAILABILITY },
            label: I18n.t("papp.products.EditProductView.menu.availability"),
            visible: initialProduct.booking_type === "static_date_booking",
          },
          {
            stepParams: { id: STEPS.STEP_PRICELIST },
            label: (
              <span className={classNames({ "admin-link": isAccommodation && isAdmin })}>
                {I18n.t("papp.products.EditProductView.menu.prices")}
              </span>
            ),
            visible: !isAccommodation || isAdmin,
          },
          {
            stepParams: { id: STEPS.STEP_BOOKING_SETTINGS },
            label: I18n.t("papp.products.EditProductView.menu.booking_settings"),
          },
        ],
      },
      {
        stepParams: { id: STEPS.STEP_PHOTOS },
        label: I18n.t("papp.products.EditProductView.menu.photos"),
        icon: <i className="icon material-icons">photo_library</i>,
      },
      {
        stepParams: { id: STEPS.STEP_LOCATION },
        label: I18n.t("papp.products.EditProductView.menu.location"),
        icon: <i className="icon material-icons">map</i>,
      },
      {
        stepParams: { id: STEPS.STEP_PRICED_FIELDS },
        label: I18n.t("papp.products.EditProductView.menu.priced_fields"),
        icon: <i className="icon material-icons">add_shopping_cart</i>,
        visible: initialProduct.product_type !== "coupon",
      },
      {
        stepParams: { id: STEPS.STEP_ADDITIONAL },
        label: (
          <span className={classNames({ "admin-link": isAdmin })}>
            {I18n.t("papp.products.EditProductView.menu.additional_settings")}
          </span>
        ),
        icon: <StLogo icon black className="icon" />,
        visible: st.authorize("update_st_details", initialProduct),
        children: [
          {
            stepParams: { id: STEPS.STEP_PAYMENTS },
            label: (
              <span className={classNames({ "admin-link": isAdmin })}>
                {I18n.t("papp.products.EditProductView.menu.payments")}
              </span>
            ),
            visible: isAdmin && initialProduct.has_pricing,
          },
        ],
      },
      {
        stepParams: { id: STEPS.STEP_INTEGRATIONS },
        label: I18n.t("papp.products.EditProductView.menu.integrations"),
        icon: <i className="icon material-icons">account_tree</i>,
        visible: product.connectly_source_name !== null && isAdmin,
      },
    ];
    if (isStandard || isAdmin) {
      menuSteps.push({
        stepParams: { id: STEPS.STEP_CUSTOMER_FIELDS },
        label: (
          <span className={classNames({ "admin-link": !isStandard && isAdmin })}>
            {I18n.t("papp.products.EditProductView.menu.customer_fields")}
          </span>
        ),
        icon: <i className="icon material-icons">library_books</i>,
      });
    }
    return menuSteps.map(createLink).filter((l) => !!l);
  }

  changeProductStatus(status, value) {
    const { product, updateProduct, validate } = this.props;
    if (status === "published_on_saas") {
      updateProduct({
        ...product,
        [status]: value,
      });
    } else {
      updateProduct({
        ...product,
        status,
      });
      validate({ submitValidate: true });
    }
  }

  renderSubBar() {
    const {
      currentCompanyId,
      initialProduct,
      product,
      submitLoading,
      dirty,
      valid,
      remove,
      save,
      currentStepParams,
      toggleSidebar,
      forceSidebarVisibility,
      fetchDone,
    } = this.props;
    const readOnlyAccess = product ? !st.authorize("update", product) : undefined;

    const handleRemoveClick = () =>
      st
        .confirm({
          content: I18n.t("papp.products.EditProductView.common.remove_confirm_message"),
        })
        .then((yes) => yes && remove());

    const isStepAvailability = currentStepParams && currentStepParams.id === STEPS.STEP_AVAILABILITY;

    const getPreviewUrlWithSlug = (url) => {
      const productId = initialProduct.id;
      const productSlug = initialProduct.slug;
      const urlWithSLug = url.split("/p/")[0] + `/p/${productId}-${productSlug}`;
      return urlWithSLug;
    };

    const getPreview = (url) => {
      return url.replace("/p/", "/pl/p/");
    };
    return (
      <SubBar>
        <a onClick={toggleSidebar.bind(null, true)} className="sidebar-toggle">
          <span className="label">{I18n.t("papp.products.EditProductView.menu.title")}</span>
          <i className="icon material-icons">chevron_right</i>
        </a>

        <div className="u-displayFlex">
          <h1 className="u-flexGrow u-textOverflow u-marginRight">{initialProduct && initialProduct.name}</h1>
          {!!initialProduct && product.connectly_source_name && (
            <div style={{ marginRight: "10px" }}>
              <Label color="yellow">{product.connectly_source_name}</Label>
            </div>
          )}
          {fetchDone && (
            <div className="u-textAlignRight u-whiteSpaceNowrap">
              {!!initialProduct && (
                <Button
                  as={Link}
                  to={`/c/${currentCompanyId}/orders?product_id=${encodeURI(initialProduct.id)}`}
                  className="u-marginRight"
                  content={I18n.t("papp.products.EditProductView.common.orders_button")}
                />
              )}
              {!readOnlyAccess && !!initialProduct && !product.active && (
                <Button
                  as="a"
                  basic
                  size="tiny"
                  color="red"
                  disabled={submitLoading}
                  className="u-marginRight"
                  onClick={handleRemoveClick}
                  content={I18n.t("papp.common.remove")}
                />
              )}
              {!readOnlyAccess && (
                <FormControl.Checkbox
                  // hidden={isStepAvailability}
                  checked={product.active}
                  onChange={(checked) => this.changeProductStatus(checked ? "published" : "draft")}
                  label={I18n.humanAttributeName("product", "active")}
                  toggle
                  className="u-marginRight"
                />
              )}

              {st.authorize("update", product) && product.company.enable_saas && (
                <FormControl.Checkbox
                  checked={product.active && product.published_on_saas}
                  onChange={(checked) => this.changeProductStatus("published_on_saas", checked)}
                  label={I18n.t("papp.components.ProductsListItem.published_on_saas_switch_label")}
                  toggle
                  disabled={!product.active}
                  className="u-marginRight"
                />
              )}

              {!!initialProduct &&
                st.authorize("preview_marketplace", initialProduct) &&
                initialProduct.marketplace_preview_url && (
                  <Button
                    as="a"
                    target="_blank"
                    disabled={submitLoading}
                    className="u-marginRight"
                    href={getPreviewUrlWithSlug(initialProduct.marketplace_preview_url)}
                    icon={<i className="icon material-icons">remove_red_eye</i>}
                  />
                )}
              {!!initialProduct && st.authorize("preview_saas", initialProduct) && initialProduct.saas_preview_url && (
                <Button
                  as="a"
                  target="_blank"
                  disabled={submitLoading}
                  className="u-marginRight"
                  href={getPreviewUrlWithSlug(initialProduct.saas_preview_url)}
                  content={I18n.t("papp.products.EditProductView.common.preview_saas")}
                />
              )}
              {!readOnlyAccess && !isStepAvailability && (
                <Button
                  as="a"
                  primary
                  disabled={submitLoading || !dirty}
                  onClick={save}
                  content={I18n.t("papp.common.save_changes")}
                />
              )}
            </div>
          )}
        </div>
      </SubBar>
    );
  }

  handleEditedLocaleChange(editedLocale) {
    if (editedLocale) {
      this.props.changeEditedLocale(editedLocale);
    } else {
      openManageLocalesPrompt({
        locales: this.props.product.available_locales,
      }).then((nextLocales) => {
        nextLocales && this.props.setLocales(nextLocales);
      });
    }
  }

  handleSelectSourceLanguage = (value) => {
    this.setState({ sourceLanguage: value });
  };

  handleToggleModal = (value) => {
    this.setState({ isModalOpen: value });
  };

  translateProduct = () => {
    const { product, editedLocale, updateProduct, updateTranslatedProduct } = this.props;

    this.setState({
      isTranslating: true,
    });

    const translatedProduct = ProductsRepository.translate(product.id, this.state.sourceLanguage, editedLocale).subscribe(
      async (res) => {
        updateTranslatedProduct(res);

        this.handleToggleModal(false);
        this.setState({
          isTranslating: false,
        });
      },
      (error) => {
        this.handleToggleModal(false);
        this.setState({
          isTranslating: false,
        });
        
        const alertUuid = `${UPDATE_PRODUCT_FAILURE}-${product.id}`;
        store.dispatch(
          createAlert({
            type: "error",
            uuid: alertUuid,
            content: st.api.findResponseErrorMessage(error),
            timeout: false,
          })
        );
      }
    );

    this.subscriptions.push(translatedProduct);
  };

  render() {
    const {
      initialProduct,
      steps,
      product,
      fetchDone,
      fetchLoading,
      fetchFailed,
      submitLoading,
      valid,
      requestStep,
      remove,
      save,
      sidebarExtended,
      forceSidebarVisibility,
      toggleSidebar,
      currentStepParams,
      editedLocale,
      changeEditedLocale,
    } = this.props;
    const sidemenuLinks = this.generateSidemenuLinks();

    return (
      <SidebarView.Container>
        <SidebarView.Sidebar
          label={I18n.t("papp.products.EditProductView.menu.title")}
          hasToggle={!forceSidebarVisibility}
          extended={sidebarExtended}
          onToggle={toggleSidebar}
        >
          {fetchDone && product && (
            <div className="c-EditProductView-sidebar-locale-switch">
              <FormControl.LocaleSelectFormGroup
                value={editedLocale}
                onChange={this.handleEditedLocaleChange.bind(this)}
                locales={product.available_locales}
                includeBlank
                placeholder={I18n.t("papp.products.EditProductView.menu.add_locale")}
                fluid
                className="c-inputStandard"
              />

              <AutoTranslateButton handleToggleModal={() => this.handleToggleModal(true)} />

              {this.state.isModalOpen && (
                <Modal closeIcon open={this.state.isModalOpen} onClose={() => this.handleToggleModal(false)}>
                  <Modal.Header>{I18n.t("papp.products.EditProductView.menu.translate_modal.title")}</Modal.Header>

                  <Modal.Content>
                    <p>
                      {I18n.t("papp.products.EditProductView.menu.translate_modal.explanation")}
                    </p>

                    <FormControl.LocaleSelectFormGroup
                      label={I18n.t("papp.products.EditProductView.menu.translate_modal.select_source_locale")}
                      value={this.state.sourceLanguage}
                      onChange={this.handleSelectSourceLanguage.bind(this)}
                      locales={product.available_locales}
                      placeholder={I18n.t("papp.products.EditProductView.menu.translate_modal.select_source_locale")}
                      fluid
                      className="c-inputStandard"
                      id="target-locale"
                      disabled={this.state.isTranslating}
                    />

                    <br />

                    <FormControl.LocaleSelectFormGroup
                      label={I18n.t("papp.products.EditProductView.menu.translate_modal.select_locale")}
                      value={editedLocale}
                      onChange={this.handleEditedLocaleChange.bind(this)}
                      locales={product.available_locales}
                      placeholder={I18n.t("papp.products.EditProductView.menu.translate_modal.select_locale")}
                      fluid
                      className="c-inputStandard"
                      id="target-locale"
                      disabled={this.state.isTranslating}
                    />
                  </Modal.Content>

                  <Modal.Actions>
                    <Button
                      primary
                      onClick={() => {
                        this.translateProduct();
                      }}
                      disabled={this.state.isTranslating}
                    >
                      {I18n.t("papp.products.EditProductView.menu.translate_modal.submit")}{" "}
                      {this.state.isTranslating && (
                        <Loader size='tiny' inline active={this.state.isTranslating} />
                      )}
                    </Button>
                  </Modal.Actions>
                </Modal>
              )}
            </div>
          )}
          <SidebarView.Menu links={sidemenuLinks} />
        </SidebarView.Sidebar>
        <SidebarView.Content hasSidebar={forceSidebarVisibility}>
          {this.renderSubBar()}
          <MainView.Content padded={false} hasSubBar>
            <MainView.Dimmer loading={fetchLoading} failed={fetchFailed} retry={this.props.fetchProduct} />
            {fetchDone && this.renderStepContent()}
          </MainView.Content>
        </SidebarView.Content>
      </SidebarView.Container>
    );
  }
}



// WEBPACK FOOTER //
// ./src/modules/products/EditProductView/EditProductView.jsx